import { PayloadAction } from '@reduxjs/toolkit';
import { call, select } from 'redux-saga/effects';
import { SignalingSocket } from 'services/signaling';
import {
  selectIsContentLocked,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { ContentData } from 'features/layout/features/content/types';
import { LOCKED_ZOOM_CONTENT_TYPES } from 'features/content-zoom/constants';
import { PermissionTypes } from 'features/permissions/types';
import { selectZoomState } from 'features/content-zoom/contentZoomSlice';
import { ZoomState } from 'features/content-zoom/types';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { buildZoomId, toServerScale } from 'features/content-zoom/utils/formatUtils';

export function* sendLockedContentZoomSaga(action: PayloadAction<{ zoomId: string }>) {
  const isContentLocked: boolean = yield select(selectIsContentLocked);

  if (!isContentLocked) {
    return;
  }

  const lockedContent: ContentData | null = yield select(selectLockedContent);

  if (!lockedContent || !LOCKED_ZOOM_CONTENT_TYPES.includes(lockedContent.type)) {
    return;
  }

  const lockedZoomId = buildZoomId(lockedContent.id, lockedContent.page);

  // Only broadcast zoom that targets the locked content; the moderator may be
  // viewing another file locally that remote users cannot see.
  if (action.payload.zoomId !== lockedZoomId) {
    return;
  }

  const canLockContent: boolean = yield call(hasPermissionsSaga, PermissionTypes.lockContent);

  if (!canLockContent) {
    return;
  }

  const zoomData: ZoomState | undefined = yield select(selectZoomState, lockedZoomId);

  if (!zoomData) {
    return;
  }

  yield call(SignalingSocket.send, {
    event: 'updateLockedContentZoom',
    data: {
      scale: toServerScale(zoomData.scale),
      offsetX: zoomData.offsetX,
      offsetY: zoomData.offsetY,
    },
  });
}
